import { Banner, Button, Surface, Text } from '@cloudflare/kumo'
import { ArrowsClockwise } from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'
import { adminApi, type GenerationWorkflowStatus, type SimulationDetail } from '../api'
import { useAdminSessionKey, useGetToken, type GetToken } from '../lib/auth'
import {
  canRecoverGeneration,
  createGenerationRecovery,
  INITIAL_RECOVERY_VIEW,
} from '../lib/generation-recovery'
import { ConfirmDialog } from './confirm-dialog'

const WORKFLOW_LABEL: Record<NonNullable<GenerationWorkflowStatus['workflowStatus']>, string> = {
  queued: '대기',
  running: '실행 중',
  paused: '일시 정지',
  errored: '오류로 종료',
  terminated: '종료됨',
  complete: '실행 완료',
  waiting: '대기 중',
  waitingForPause: '일시 정지 대기',
  unknown: '상태 미확인',
}

export function GenerationRecovery({
  simulationId,
  onChanged,
}: {
  simulationId: string
  onChanged: (simulation: SimulationDetail) => void
}) {
  const getToken = useGetToken()
  const sessionKey = useAdminSessionKey()
  const [view, setView] = useState(INITIAL_RECOVERY_VIEW)
  const changed = useRef(onChanged)
  changed.current = onChanged
  const rendered = useRef({ simulationId, getToken, sessionKey })
  rendered.current = { simulationId, getToken, sessionKey }
  const active = useRef<{
    id: string
    getToken: GetToken
    sessionKey: string | null
    controller: ReturnType<typeof createGenerationRecovery>
  } | null>(null)
  useEffect(() => {
    const controller = createGenerationRecovery(
      {
        refresh: async () => {
          const [status, { simulation }] = await Promise.all([
            adminApi.simulations.workflow(getToken, simulationId),
            adminApi.simulations.get(getToken, simulationId),
          ])
          return { status, simulation }
        },
        recover: () => adminApi.simulations.recover(getToken, simulationId),
        changed: (simulation) => changed.current(simulation),
      },
      setView,
      () =>
        active.current?.controller === controller &&
        rendered.current.simulationId === simulationId &&
        rendered.current.getToken === getToken &&
        rendered.current.sessionKey === sessionKey,
    )
    active.current = { id: simulationId, getToken, sessionKey, controller }
    void controller.check()
    return () => {
      controller.dispose()
      if (active.current?.controller === controller) active.current = null
    }
  }, [getToken, sessionKey, simulationId])
  const current = () =>
    active.current?.id === simulationId &&
    active.current.getToken === getToken &&
    active.current.sessionKey === sessionKey
      ? active.current.controller
      : null
  const busy = view.busy !== null

  return (
    <Surface className="mb-4 flex flex-col gap-3 rounded-lg p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Text variant="heading">생성 작업 상태</Text>
        <Button
          variant="secondary"
          size="sm"
          icon={<ArrowsClockwise />}
          disabled={busy}
          loading={view.busy === 'checking'}
          onClick={() => void current()?.check()}
        >
          작업 상태 조회
        </Button>
      </div>
      <div role="status" aria-live="polite" className="text-sm">
        {view.status
          ? `Workflow: ${view.status.workflowStatus ? WORKFLOW_LABEL[view.status.workflowStatus] : '조회 불가'}${view.status.attempt === null ? '' : ` · 시도 ${view.status.attempt + 1}`}`
          : busy
            ? '작업 상태를 확인하고 있어요.'
            : '작업 상태를 확인하지 못했어요.'}
      </div>
      {view.status?.simulationStatus === 'cancelled' && (
        <Banner
          variant="default"
          title="사용자가 취소한 생성"
          description="대기 중에 취소했으면 생성 사용권을 돌려줬고, AI 가 시작한 뒤 취소했으면 돌려주지 않았어요. 정리할 작업이 없어요."
        />
      )}
      <Text variant="secondary" size="sm">
        서버가 오류 종료 또는 종료됨을 확인한 미완료 작업만 정리해요. 대기 시간만으로 실패를
        판단하지 않으며, 새 AI 생성을 시작하는 재실행과는 달라요.
      </Text>
      {view.error && <Banner variant="error" title="상태 확인 필요" description={view.error} />}
      {view.notice && <Banner variant="default" title="복구 처리 결과" description={view.notice} />}
      <div>
        <Button
          variant="secondary"
          disabled={busy || !canRecoverGeneration(view.status)}
          onClick={() => current()?.ask()}
        >
          종료된 생성 작업 복구
        </Button>
      </div>
      <ConfirmDialog
        open={view.confirmation}
        title="종료된 생성 작업을 정리할까요?"
        description="현재 작업이 오류로 종료되었는지 서버에서 다시 확인해요. 저장된 완료 결과가 있으면 보존하고, 새 AI 작업은 시작하지 않아요. 생성 상태가 바뀌었으면 복구하지 않아요."
        confirmLabel="확인 후 복구"
        busy={busy}
        onConfirm={() => void current()?.recover()}
        onClose={() => current()?.cancel()}
      />
    </Surface>
  )
}
